import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangleIcon, Loader2Icon, LockIcon, PlugZapIcon, SendIcon } from 'lucide-react';
import { BookingForm } from '../../hooks/useBookingForm';
import { formatNaira } from '../../utils/format';
import { BookingResult } from './BookingResult';
export function SubmitPanel({
  form
}: {
  form: BookingForm;
}) {
  if (form.result) return <BookingResult result={form.result} onEdit={form.editAfterRejection} data-id="element-258" data-mp="7z3p0p5c7kh6" />;
  const live = form.integrationStatus === 'ready';
  const redirecting = form.submitStatus === 'redirecting';
  const submitting = form.submitStatus === 'submitting' || redirecting;
  return <div data-id="element-259" data-mp="gy75rmh17nsi">
      <div className="rounded-sm bg-ivory-100 p-6" data-id="element-260" data-mp="cyq310szp4r1">
        <p className="text-[11px] uppercase tracking-overline text-gold-700" data-id="element-261" data-mp="d0142sv1vyeg">Ready to send</p>
        <p className="mt-2 font-display text-2xl text-forest-700" data-id="element-262" data-mp="2isfyh714v3za">
          {form.isStay ? form.property.name : 'Photoshoot session'} · {formatNaira(form.estimate.total)}
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-263" data-mp="168n3sghlde30">
          Estimated total. Your request is sent to the Bel Air booking system, which checks availability, calculates final
          charges and records the booking as Pending until payment is verified.
        </p>
      </div>

      {!live && <div className="mt-6 flex items-start gap-3 rounded-sm border border-gold-400/50 bg-gold-100 p-5 text-[13px] leading-relaxed text-forest-700" data-id="element-264" data-mp="1kxjkt31mm1fr">
          <PlugZapIcon size={18} className="mt-0.5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-265" data-mp="1txg0wg9y9rz7" />
          <div data-id="element-266" data-mp="1ywj9gap52gbx">
            <p className="font-normal" data-id="element-267" data-mp="1ywsqv9w4bf60">Online booking is paused.</p>
            <p className="mt-1 text-muted" data-id="element-268" data-mp="1yugc15lnpkx6">
              Please contact us on WhatsApp or by phone to complete your booking — your details above are ready to share.
            </p>
          </div>
        </div>}

      <AnimatePresence data-id="element-269" data-mp="9hl7gmzf0t8q">
        {form.submitError && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} role="alert" className="mt-6 flex items-start gap-3 rounded-sm bg-danger-50 p-4 text-[14px] leading-relaxed text-danger-700" data-id="element-270" data-mp="9i4m9utrr1ij">
            <AlertTriangleIcon size={17} className="mt-0.5 shrink-0" aria-hidden="true" data-id="element-271" data-mp="6g9kim3i9w68" />
            <p data-id="element-272" data-mp="kfrfjj7ea5o1">{form.submitError}</p>
          </motion.div>}
      </AnimatePresence>

      <button type="button" onClick={() => void form.submit()} disabled={!live || submitting} aria-disabled={!live || submitting} className="mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-forest-700 px-8 py-4 text-sm text-ivory-100 transition-colors duration-150 ease-premium hover:bg-forest-600 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-forest-200 disabled:text-forest-500 sm:w-auto" data-id="element-273" data-mp="kdes4qivb33l">
        {submitting ? <>
            <Loader2Icon size={16} className="animate-spin" aria-hidden="true" data-id="element-274" data-mp="1ipxldbzea5gu" />
            {redirecting ? 'Opening secure Paystack checkout…' : 'Creating your booking…'}
          </> : <>
            <SendIcon size={15} aria-hidden="true" data-id="element-275" data-mp="18t2mgrz7m321" />
            {live ? 'Continue to secure payment' : 'Online booking paused'}
          </>}
      </button>
      <p className="mt-3 flex items-start gap-2 text-[12px] leading-relaxed text-muted" aria-live="polite" data-id="element-276" data-mp="z2hs5gqkjkox">
        <LockIcon size={13} className="mt-0.5 shrink-0 text-gold-500" aria-hidden="true" data-id="element-277" data-mp="13sy56zqh94ar" />
        {redirecting ? 'Redirecting you to Paystack. Please don’t close this page.' : 'Your booking is created first, then you’re taken to Paystack to pay securely. Final amounts are calculated by our booking system, and your booking is confirmed only after Paystack payment is verified.'}
      </p>
    </div>;
}